import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  CalendarBlank, ArrowLeft, FloppyDisk, ChartLineUp, CheckCircle, Warning,
  TrashSimple, CaretLeft, CaretRight, House,
} from "@phosphor-icons/react";

// Dead code: WRITE_ROLES ve DELETE_ROLES artık kullanılmıyor; modüle erişimi
// olan tüm kullanıcılar yazma işlemlerini yapabilir (ModuleGuard route-level).

function flattenLeaves(items, path = []) {
  const out = [];
  for (const it of items || []) {
    const p = [...path, it.name];
    if (it.children?.length > 0) {
      out.push(...flattenLeaves(it.children, p));
    } else {
      out.push({ ...it, breadcrumb: p.slice(0, -1).join(" › ") });
    }
  }
  return out;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function ymRange(date) {
  const d = new Date(date + "T00:00:00");
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const toISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  return { from: toISO(first), to: toISO(last), year: d.getFullYear(), month: d.getMonth() };
}

export default function BlockDailyProduction() {
  const { siteId, blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const canWrite = !!user;
  const canDelete = !!user;

  const [meta, setMeta] = useState({ block_name: "", block_code: "" });
  const [leaves, setLeaves] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [monthEntries, setMonthEntries] = useState([]);   // ay içindeki tüm girişler
  const [dayQty, setDayQty] = useState({});                // {wbs_item_id: qty}  seçili gün
  const [dayNote, setDayNote] = useState({});              // {wbs_item_id: note}  seçili gün
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const range = ymRange(selectedDate);
      const [w, p, dpr] = await Promise.all([
        axios.get(`${API}/blocks/${blockId}/wbs`),
        axios.get(`${API}/blocks/${blockId}/progress`),
        axios.get(`${API}/blocks/${blockId}/daily-production`, {
          params: { from: range.from, to: range.to },
        }),
      ]);
      setMeta({ block_name: w.data.block_name, block_code: w.data.block_code });
      setLeaves(flattenLeaves(w.data.items || []));
      setProgress(p.data);
      setMonthEntries(dpr.data || []);
      // Seçili günün girişlerini form state'e yükle
      const dq = {}, dn = {};
      for (const e of dpr.data || []) {
        if (e.date === selectedDate) {
          dq[e.wbs_item_id] = e.qty;
          dn[e.wbs_item_id] = e.note || "";
        }
      }
      setDayQty(dq); setDayNote(dn);
      setDirty(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [blockId, selectedDate]);

  const changeMonth = (delta) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setMonth(d.getMonth() + delta);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  // Ay içindeki tüm günler
  const monthDays = useMemo(() => {
    const r = ymRange(selectedDate);
    const days = [];
    let cur = new Date(r.from + "T00:00:00");
    const last = new Date(r.to + "T00:00:00");
    while (cur <= last) {
      const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      days.push(iso);
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [selectedDate]);

  const dayHasEntry = useMemo(() => {
    const s = new Set();
    for (const e of monthEntries) s.add(e.date);
    return s;
  }, [monthEntries]);

  const setQty = (wid, v) => {
    setDayQty(q => ({ ...q, [wid]: v }));
    setDirty(true);
  };
  const setNote = (wid, v) => {
    setDayNote(n => ({ ...n, [wid]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const entries = [];
      for (const [wid, v] of Object.entries(dayQty)) {
        const qty = parseFloat(v);
        if (qty > 0) {
          entries.push({ wbs_item_id: wid, qty, note: dayNote[wid] || "" });
        }
      }
      await axios.post(`${API}/blocks/${blockId}/daily-production`, {
        date: selectedDate,
        entries,
      });
      toast.success(`${selectedDate} kaydedildi (${entries.length} kalem)`);
      setDirty(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const clearDay = async () => {
    const ok = await askConfirm({
      title: "Bu Günü Sil",
      description: `${selectedDate} tarihine ait tüm imalat girişleri silinsin mi?`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/blocks/${blockId}/daily-production/${selectedDate}`);
      toast.success("Gün silindi");
      setDayQty({}); setDayNote({});
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Kümülatif done_qty (item bazında) — progress endpoint'inden geliyor
  const cumulativeByItem = useMemo(() => {
    const m = {};
    for (const it of (progress?.items || [])) {
      m[it.wbs_item_id] = it;
    }
    return m;
  }, [progress]);

  // Bu ay o kalem için kümülatif (monthEntries'ten hesapla)
  const monthQtyByItem = useMemo(() => {
    const m = {};
    for (const e of monthEntries) {
      m[e.wbs_item_id] = (m[e.wbs_item_id] || 0) + (Number(e.qty) || 0);
    }
    return m;
  }, [monthEntries]);

  const monthDayCount = dayHasEntry.size;

  return (
    <Shell
      title={meta.block_name ? `${meta.block_name} — Günlük İmalat` : "Yükleniyor…"}
      subtitle="Proje İlerleme › Takvim"
      hideSiteSelector
      actions={
        <Button variant="outline" onClick={() => navigate(`/proje/${siteId}/yapisi`)}
                className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
          <ArrowLeft size={14} className="mr-1.5" /> Yapıya Dön
        </Button>
      }
    >
      {/* Üst özet band */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Blok</div>
          <div className="font-bold text-slate-900 truncate">{meta.block_name}</div>
          <div className="text-xs text-slate-500 font-mono">{meta.block_code || "—"}</div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Ödemeye Esas Pursantaj</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1 font-heading" data-testid="progress-price">
            %{((progress?.progress_by_price || 0) * 100).toFixed(1)}
          </div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Fiziki Pursantaj</div>
          <div className="text-2xl font-bold text-[#95C11F] mt-1 font-heading" data-testid="progress-qty">
            %{((progress?.progress_by_qty || 0) * 100).toFixed(1)}
          </div>
        </Card>
        <Card className="p-3 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Bu Ay Giriş</div>
          <div className="text-2xl font-bold text-slate-800 mt-1 font-heading" data-testid="month-days">
            {monthDayCount} <span className="text-xs text-slate-500 font-normal">gün</span>
          </div>
        </Card>
      </div>

      {leaves.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <ChartLineUp size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">Bu blokta iş kırınım kalemleri yok</div>
          <div className="text-xs text-slate-500 mb-4">
            Önce blok'un WBS'ini tanımlayın, sonra buradan günlük imalat girebilirsiniz.
          </div>
          <Button onClick={() => navigate(`/proje/${siteId}/blok/${blockId}/wbs`)}
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
            WBS Tanımla
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4">
          {/* Sol: Takvim */}
          <Card className="rounded-none sm:rounded-sm border-slate-200">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <button onClick={() => changeMonth(-1)} data-testid="prev-month"
                      className="w-8 h-8 flex items-center justify-center hover:bg-white">
                <CaretLeft size={16} />
              </button>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-1.5">
                <CalendarBlank size={14} weight="duotone" className="text-[#95C11F]" />
                {new Date(selectedDate + "T00:00:00").toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}
              </div>
              <button onClick={() => changeMonth(1)} data-testid="next-month"
                      className="w-8 h-8 flex items-center justify-center hover:bg-white">
                <CaretRight size={16} />
              </button>
            </div>
            <div className="p-2 grid grid-cols-7 gap-1">
              {["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"].map(d => (
                <div key={d} className="text-[9px] uppercase tracking-widest font-bold text-slate-500 text-center py-1">{d}</div>
              ))}
              {monthDays.map((iso, i) => {
                const d = new Date(iso + "T00:00:00");
                const dow = d.getDay(); // 0 = Sun
                const dayInMonth = d.getDate();
                // Türk takvimi: Pt=1..Pz=7; JS: Pz=0
                const offset = i === 0 ? (dow === 0 ? 6 : dow - 1) : 0;
                const hasEntry = dayHasEntry.has(iso);
                const isSelected = iso === selectedDate;
                const isToday = iso === todayISO();
                return (
                  <React.Fragment key={iso}>
                    {i === 0 && Array.from({ length: offset }, (_, j) => <div key={`sp-${j}`} />)}
                    <button
                      onClick={() => setSelectedDate(iso)}
                      data-testid={`day-${iso}`}
                      className={`aspect-square flex flex-col items-center justify-center text-xs font-mono relative
                        ${isSelected ? "bg-[#1B4776] text-white font-bold" :
                          hasEntry ? "bg-[#F7FBE9] text-[#1B4776] font-bold border border-[#95C11F]" :
                          "hover:bg-slate-100 text-slate-700"}
                        ${isToday && !isSelected ? "ring-2 ring-[#95C11F]/60" : ""}`}
                    >
                      {dayInMonth}
                      {hasEntry && !isSelected && (
                        <div className="absolute bottom-0.5 w-1.5 h-1.5 rounded-full bg-[#95C11F]" />
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="p-3 border-t border-slate-200 text-[10px] text-slate-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#95C11F]"></span> Giriş var</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-none border border-[#95C11F]"></span> Bugün</span>
            </div>
          </Card>

          {/* Sağ: Seçili gün girişleri */}
          <Card className="rounded-none sm:rounded-sm border-slate-200">
            <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Seçili Gün</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid="selected-day">
                  {new Date(selectedDate + "T00:00:00").toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>
              {canWrite && (
                <div className="flex gap-2">
                  {canDelete && dayHasEntry.has(selectedDate) && (
                    <Button onClick={clearDay} size="sm" variant="outline"
                            data-testid="clear-day"
                            className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest text-red-600 border-red-200 hover:bg-red-50">
                      <TrashSimple size={12} className="mr-1" /> Günü Sil
                    </Button>
                  )}
                  <Button onClick={save} disabled={saving || !dirty}
                          data-testid="save-day"
                          size="sm"
                          className="rounded-none sm:rounded-sm h-8 text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] font-bold disabled:opacity-40">
                    <FloppyDisk size={12} className="mr-1" />
                    {saving ? "Kaydediliyor..." : dirty ? "Kaydet" : "Kayıtlı"}
                  </Button>
                </div>
              )}
            </div>
            {dirty && (
              <div className="mx-3 mt-3 bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
                <Warning size={14} weight="fill" /> Kaydedilmemiş değişiklikler var.
              </div>
            )}
            <div className="p-3 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-sm text-slate-500">Yükleniyor…</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 px-2">Kod</th>
                      <th className="text-left py-2 px-2">İmalat</th>
                      <th className="text-right py-2 px-2">Planlanan</th>
                      <th className="text-right py-2 px-2">Kümülatif</th>
                      <th className="text-right py-2 px-2 bg-[#F7FBE9]">Bu Gün</th>
                      <th className="text-right py-2 px-2">İlerleme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.map(l => {
                      const cum = cumulativeByItem[l.id] || {};
                      const doneAll = cum.done_qty || 0;
                      const prog = cum.progress || 0;
                      const cur = dayQty[l.id];
                      return (
                        <tr key={l.id} data-testid={`prod-row-${l.id}`}
                            className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1.5 px-2 font-mono text-xs text-slate-500">{l.code}</td>
                          <td className="py-1.5 px-2">
                            <div className="font-medium text-slate-900 text-xs">{l.name}</div>
                            {l.breadcrumb && <div className="text-[10px] text-slate-400 truncate">{l.breadcrumb}</div>}
                          </td>
                          <td className="py-1.5 px-2 text-right text-xs font-mono text-slate-700 whitespace-nowrap">
                            {(l.planned_qty || 0).toLocaleString("tr-TR")} <span className="text-slate-400">{l.unit}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-xs font-mono">
                            <span className="text-[#1B4776] font-bold">{doneAll.toLocaleString("tr-TR")}</span>
                            <span className="text-slate-400"> / {(l.planned_qty || 0).toLocaleString("tr-TR")}</span>
                          </td>
                          <td className="py-1.5 px-2 bg-[#F7FBE9]/50">
                            {canWrite ? (
                              <Input
                                type="number" min="0" step="0.01"
                                value={cur ?? ""}
                                data-testid={`prod-qty-${l.id}`}
                                onChange={e => setQty(l.id, e.target.value)}
                                placeholder="0"
                                className="h-7 rounded-none sm:rounded-sm text-right font-mono w-24"
                              />
                            ) : (
                              <div className="text-right font-mono">{cur || "-"}</div>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            <div className="text-xs font-bold font-mono" style={{
                              color: prog >= 1 ? "#95C11F" : prog > 0 ? "#1B4776" : "#94a3b8"
                            }} data-testid={`prod-progress-${l.id}`}>
                              %{(prog * 100).toFixed(1)}
                            </div>
                            {prog >= 1 && <CheckCircle size={11} weight="fill" className="text-[#95C11F] inline" />}
                            <div className="h-1 bg-slate-200 mt-0.5 relative overflow-hidden">
                              <div className="absolute inset-y-0 left-0 bg-[#95C11F]"
                                   style={{ width: `${Math.min(prog * 100, 100)}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>
      )}
    </Shell>
  );
}
